import { Theme, css, useTheme } from '@emotion/react';

import { GetServerSidePropsContext, NextPage } from 'next';
import { useRouter } from 'next/router';

import { Suspense, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import {
  BROADCAST_CHANNEL,
  useBroadcastChannel,
} from '@hooks/useBroadcastChannel';
import { useMounted } from '@hooks/useMounted';
import { captureException } from '@sentry/nextjs';
import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import { CheckCircle, Icon, Pencil } from 'chatie-design-system/icon';

import HomeHeader from '@components/common/HomeHeader';
import { ITab, useTab } from '@components/common/Tab';
// import ChatieRankingStoryList from '@components/home/ChatieRankingStoryList';
import HomeBanner from '@components/home/HomeBanner';
import HomeHeaderContainer from '@components/home/HomeHeaderContainer';
import RecommendationSectionList from '@components/home/RecommendationSectionList';
import WebtoonStoryList from '@components/home/WebtoonStoryList';
import { homePagePrefetcher } from '@components/home/homePagePrefetcher';
import { homeTabId } from '@components/home/homeTabId';
import Layout from '@components/layout/Layout';
// import { withAuth } from '@contexts/auth';
import { PushSize } from '@components/layout/Push';
import SafeArea from '@components/layout/SafeArea';
import Frame from '@components/skeleton/Frame';
import PlaySkeleton from '@components/skeleton/PlaySkeleton';
import SkeletonList from '@components/skeleton/SkeletonList';
import { SectionStoryList } from '@components/stories/SectionStoryTab';

import { fetchSections } from '@api/section';
import { setupPerfReporting } from '@base/lib/measureWebPerf';
import { Platform, QUERY_KEYS, SectionType } from '@constants';
import { withAuth } from '@contexts/auth';
import { useGlobalContext } from '@contexts/global';
import { responsiveMaxWidth, safeArea } from '@styles/css';
import { useAnalytics } from '@utils/analytics';
import { isEnv, openDeepLink } from '@utils/misc';
import {
  combineProps,
  withDehydrate,
  withGetServerSideProps,
} from '@utils/ssr';

export const HomeLoading = () => {
  return (
    <>
      <Layout>
        <HomeHeader hasNewNotification={false} isHome={true} />
        <Frame css={bannerSkeletonCSS} />
        <SkeletonList skeleton={PlaySkeleton} />
      </Layout>
    </>
  );
};

export const ListLoading = () => {
  return (
    <>
      <Frame css={bannerSkeletonCSS} />
      <SkeletonList skeleton={PlaySkeleton} />
    </>
  );
};

const homeContainerCSS = css`
  // margin-top: 42px;
  .section-container {
    margin-top: 112px;
  }
`;

const footerCSS = (theme: Theme) => css`
  margin-top: 28px;
  text-align: center;

  .title {
    display: flex;
    justify-content: center;
    gap: 2px;
    align-items: center;
  }

  p {
    color: ${theme.var.gray400};
    ${Typography.body12}
  }
`;

const floatingButtonContainerCSS = (isCheming: boolean) => (theme: Theme) =>
  css`
    ${responsiveMaxWidth};
    margin: 0 auto;
    position: relative;

    .floating-wrapper {
      display: flex;
      flex-direction: row-reverse;
      gap: 12px;
      width: 100%;
      ${responsiveMaxWidth};
      padding: 16px;
      position: fixed;
      bottom: ${isCheming ? `${PushSize.bottomTab}px` : '0'};
      z-index: 99;

      .floating-promo {
        position: static;
        display: flex;
        flex: 1;
        background: linear-gradient(55.29deg, #ff07f5 20.46%, #ed0064 82.76%);
      }

      .floating-create {
        display: flex;
        justify-content: center;
        align-items: center;
        align-self: flex-end;
        position: static;
        bottom: ${Spacing.medium}px;
        right: ${Spacing.medium}px;
        width: 64px;
        height: 64px;
        border-radius: ${Radius.round}px;
        z-index: 100;
        box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.4);
        background-color: ${theme.colors.main500};
      }
    }
  `;

// const promoCSS = (isPurchaseDailyFreePass: boolean) => (theme: Theme) =>
//   css`
//     display: flex;
//     flex-direction: ${isPurchaseDailyFreePass ? 'column' : 'row'};

//     ${!isPurchaseDailyFreePass &&
//     css`
//       justify-content: space-between;
//       align-items: center;
//     `}

//     width: 100%;
//     height: 100%;
//     padding: 12.5px 10px;

//     .upper-container {
//       display: flex;
//       width: 100%;
//       justify-content: space-between;
//       align-items: center;

//       .timer {
//         display: flex;
//         justify-content: flex-end;
//         align-items: center;
//         padding: 4px 6px;
//         border-radius: 10px;
//         height: 18px;
//         background: ${theme.var.gray900};
//         color: ${theme.var.white};
//         text-align: center;
//         font-size: 10px;
//         font-style: normal;
//         font-weight: 400;
//         line-height: 100%;
//         min-width: 110px;
//       }
//     }
//     .title {
//       color: ${theme.var.white};
//       ${Typography.h6};
//       font-weight: 600;
//     }
//     .under-container {
//       display: flex;
//       // align-items: flex-;
//       color: ${theme.var.white};
//       ${Typography.body11}
//     }
//     .left-container {
//       display: flex;
//       flex-direction: column;
//     }
//     .right-container {
//       display: flex;
//       flex-direction: column;
//       align-items: flex-end;
//       justify-content: space-between;
//       gap: 4px;
//     }
//     .description {
//       color: rgba(255, 255, 255, 0.9);
//       ${!isPurchaseDailyFreePass &&
//       css`
//         background-color: ${theme.var.gray900};
//         padding: 3px 4px;
//         border-radius: ${Radius.xsmall}px;
//         width: fit-content;
//         color: ${theme.var.white};
//       `}
//       margin-top: ${!isPurchaseDailyFreePass ? 4 : 8}px;
//       ${Typography.body11}
//     }

//     .price {
//       color: rgba(248, 248, 248, 0.6);

//       font-size: 10px;
//       text-decoration: line-through;
//     }
//     .discount-price {
//       color: ${theme.var.white};
//       font-size: 18px;
//       font-weight: 600;
//     }
//   `;

const HomeDetail = () => {
  const { platform, isCheming } = useGlobalContext();
  const router = useRouter();
  const { firebase } = useAnalytics();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const myBundlePassQueryKey = QUERY_KEYS.PAYMENT.MY_BUNDLE_LIST();

  useBroadcastChannel<string>(
    BROADCAST_CHANNEL.PURCHASE_DAILY_FREE_PASS,
    () => {
      queryClient.refetchQueries(myBundlePassQueryKey);
    }
  );

  const { data: sectionList } = useQuery(
    QUERY_KEYS.HOME_SECTION_LIST,
    () => fetchSections(SectionType.standard),
    {
      staleTime: 1000 * 60 * 15,
      cacheTime: 1000 * 60 * 15,
      suspense: false,
    }
  );

  const isMounted = useMounted();

  const initialTab = [
    {
      label: '추천',
      value: homeTabId.recommendation,
    },
    {
      label: '최신',
    },
    {
      label: '베스트',
    },
    {
      label: '테스트',
    },
    {
      label: '친구의연애',
    },
  ];

  const recommendTab = [
    {
      label: '추천',
      value: homeTabId.recommendation,
    },
  ];

  const parsedTab = sectionList
    ? recommendTab.concat(
        sectionList?.map((section) => {
          return {
            label: section.name,
            value: section.id,
          };
        })
      )
    : [...(initialTab as ITab<number>[])];

  const handleClickTab = (tab: number) => {
    const label = parsedTab?.find((t) => t.value === tab)?.label as string;
    firebase.logEvent('home_tab_click', { tab: label });
  };
  const tabs = useTab(parsedTab, {
    replace: false,
    withoutReRendering: false,
    onClickTab: handleClickTab,
  });
  const { activeTab = homeTabId.recommendation } = tabs;
  const normalizedActiveTab = homeTabId.normalize(activeTab);

  const handleNavigateCreate = () => {
    if (platform === Platform.web) {
      return router.push('/studio');
    } else {
      openDeepLink('chatie-app://navigation?activeTab=MYPAGE', router);
    }
  };
  return (
    <SafeArea bottom>
      <HomeHeaderContainer tabs={tabs} />
      <div css={homeContainerCSS} suppressHydrationWarning={true}>
        {normalizedActiveTab === homeTabId.recommendation && (
          <>
            <HomeBanner />
            <WebtoonStoryList />
            <RecommendationSectionList />
          </>
        )}
        {normalizedActiveTab !== homeTabId.recommendation &&
          normalizedActiveTab && (
            <Suspense fallback={<ListLoading />}>
              <div className="section-container">
                <SectionStoryList sectionId={normalizedActiveTab} />
              </div>
            </Suspense>
          )}
      </div>
      <div css={footerCSS} onClick={() => setOpen(!open)}>
        <p className="title">
          (주) 아이네블루메 사업자 정보
          <Icon
            icon={CheckCircle}
            size={Sizes.xsmall}
            color={theme.var.gray400}
          />
        </p>
        {open && (
          <p className="multiline">
            {`사업자등록번호 : 591-81-00825\n대표이사 : 최재현\n주소 :서울 강남구 테헤란로79길 6, JS타워 516호\nEmail : help@eineblu.me`}
          </p>
        )}
        <p>© EINEBLUME CO., LTD. 2018-2019 All Rights Reserved</p>
      </div>
      {isMounted && (
        <div css={floatingButtonContainerCSS(isCheming)}>
          <div className="floating-wrapper">
            <div className={'floating-create'} onClick={handleNavigateCreate}>
              <Icon
                icon={Pencil}
                color={'white'}
                onClick={handleNavigateCreate}
              />
            </div>
          </div>
        </div>
      )}
    </SafeArea>
  );
};

const layoutCSS = css`
  ${safeArea('top', 'padding', 0)};
`;

const bannerSkeletonCSS = css`
  padding-bottom: 62.5%;
`;

const Home: NextPage = () => {
  const isProduction = isEnv('production');

  useEffect(() => {
    if (typeof window !== 'undefined' && !isProduction) {
      setupPerfReporting();
    }
  }, []);

  return (
    <Layout css={layoutCSS} isSSR isPush>
      <HomeDetail />
    </Layout>
  );
};

export const getServerSideProps = withGetServerSideProps(
  async (ctx: GetServerSidePropsContext) => {
    return await combineProps(
      ctx,
      withDehydrate(async (queryClient) => {
        try {
          await homePagePrefetcher.prefetch(queryClient);

          return undefined;
        } catch (e) {
          captureException(e);
          return undefined;
        }
      })
    );
  }
);
export default withAuth(Home, {
  isSignInRequired: false,
  LoadingComponent: HomeLoading,
});
